import { useEffect, useState } from "react";
import {
    getStoredConsent,
    OPEN_PREFERENCES_EVENT,
    saveConsent,
} from "@/utils/cookieConsent";

// Banner de consentimiento de cookies (Ley 21.719): modelo opt-in.
// Ninguna cookie no esencial se activa hasta que el usuario decida.
const CookieBanner = () => {
    const [visible, setVisible] = useState(false);
    const [configurando, setConfigurando] = useState(false);
    const [analiticas, setAnaliticas] = useState(false);
    const [marketing, setMarketing] = useState(false);

    useEffect(() => {
        const stored = getStoredConsent();
        if (!stored) {
            setVisible(true);
        } else {
            setAnaliticas(stored.analiticas);
            setMarketing(stored.marketing);
        }

        const reabrir = () => {
            const actual = getStoredConsent();
            if (actual) {
                setAnaliticas(actual.analiticas);
                setMarketing(actual.marketing);
            }
            setConfigurando(true);
            setVisible(true);
        };
        window.addEventListener(OPEN_PREFERENCES_EVENT, reabrir);
        return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, reabrir);
    }, []);

    const decidir = (prefs: { analiticas: boolean; marketing: boolean }) => {
        saveConsent(prefs);
        setAnaliticas(prefs.analiticas);
        setMarketing(prefs.marketing);
        setConfigurando(false);
        setVisible(false);
    };

    if (!visible) return null;

    const botonBase =
        "flex-1 px-4 py-3 rounded-xl font-semibold text-sm transition-colors duration-200 text-center";

    return (
        <div
            className="fixed inset-x-0 bottom-0 z-[99999] p-3 sm:p-4"
            role="dialog"
            aria-modal="false"
            aria-label="Preferencias de cookies"
        >
            <div className="mx-auto max-w-3xl bg-white rounded-2xl shadow-[0_-4px_30px_rgba(0,0,0,0.18)] border border-gray-200 p-5 sm:p-6">
                <h2 className="text-base font-bold text-[#790546] mb-2">
                    🍪 Tu privacidad nos importa
                </h2>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">
                    Usamos cookies propias y de terceros. Las necesarias mantienen el sitio
                    funcionando (carrito, sesión). Las de analítica y marketing solo se activan
                    si tú lo autorizas. Puedes cambiar tu decisión cuando quieras desde
                    "Configurar cookies" en el pie de página. Más información en nuestra{" "}
                    <a
                        href="/politica-de-privacidad"
                        className="text-[#790546] underline hover:no-underline"
                    >
                        Política de Privacidad
                    </a>
                    .
                </p>

                {configurando && (
                    <div className="mb-4 space-y-3 border border-gray-100 rounded-xl p-4 bg-gray-50">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="text-sm font-semibold text-gray-800">
                                    Cookies necesarias
                                </p>
                                <p className="text-xs text-gray-500">
                                    Carrito, inicio de sesión y seguridad. Siempre activas.
                                </p>
                            </div>
                            <input
                                type="checkbox"
                                checked
                                disabled
                                className="mt-1 w-5 h-5 rounded border-gray-300 text-[#790546] opacity-60 cursor-not-allowed"
                                aria-label="Cookies necesarias (siempre activas)"
                            />
                        </div>
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="text-sm font-semibold text-gray-800">
                                    Cookies analíticas
                                </p>
                                <p className="text-xs text-gray-500">
                                    Google Analytics y Microsoft Clarity: nos ayudan a entender
                                    cómo se usa el sitio para mejorarlo.
                                </p>
                            </div>
                            <input
                                type="checkbox"
                                checked={analiticas}
                                onChange={(e) => setAnaliticas(e.target.checked)}
                                className="mt-1 w-5 h-5 rounded border-gray-300 text-[#790546] focus:ring-[#790546]/30 cursor-pointer"
                                aria-label="Cookies analíticas"
                            />
                        </div>
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="text-sm font-semibold text-gray-800">
                                    Cookies de marketing
                                </p>
                                <p className="text-xs text-gray-500">
                                    Meta Pixel (Facebook/Instagram) y TikTok Pixel: nos permiten
                                    mostrarte anuncios relevantes y medir el resultado de nuestras
                                    campañas publicitarias.
                                </p>
                            </div>
                            <input
                                type="checkbox"
                                checked={marketing}
                                onChange={(e) => setMarketing(e.target.checked)}
                                className="mt-1 w-5 h-5 rounded border-gray-300 text-[#790546] focus:ring-[#790546]/30 cursor-pointer"
                                aria-label="Cookies de marketing"
                            />
                        </div>
                    </div>
                )}

                {/* Aceptar y Rechazar con la misma jerarquía visual (exigencia anti patrón oscuro) */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <button
                        type="button"
                        onClick={() => decidir({ analiticas: true, marketing: true })}
                        className={`${botonBase} bg-[#790546] text-white hover:bg-[#6a0440]`}
                    >
                        Aceptar todo
                    </button>
                    <button
                        type="button"
                        onClick={() => decidir({ analiticas: false, marketing: false })}
                        className={`${botonBase} bg-[#790546] text-white hover:bg-[#6a0440]`}
                    >
                        Rechazar todo
                    </button>
                    {configurando ? (
                        <button
                            type="button"
                            onClick={() => decidir({ analiticas, marketing })}
                            className={`${botonBase} border-2 border-[#790546] text-[#790546] hover:bg-[#790546]/5`}
                        >
                            Guardar preferencias
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setConfigurando(true)}
                            className={`${botonBase} border-2 border-[#790546] text-[#790546] hover:bg-[#790546]/5`}
                        >
                            Configurar
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CookieBanner;
